import { format } from 'date-fns';
import React from 'react';
import { object, string } from 'yup';

import Button from '@hero/ui-kit/inputs/Button';
import Form, { useFormContext } from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';
import InputError from '@hero/ui-kit/inputs/InputError';
import Option from '@hero/ui-kit/inputs/Option';
import Select from '@hero/ui-kit/inputs/Select';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import { DrChronoTask, DrChronoTaskStatus } from '../../types';
import {
    TASK_PRIORITIES,
    TaskFormValues,
    toTaskFormValues,
    toTaskUpdate
} from '../../utils/drChronoTasks';
import * as Style from './style.module.scss';

const UNSET = '__unset__';

/** An existing due date may already be past, so keeping it unchanged must still validate. */
const getTaskSchema = (savedDueDate: string) =>
    object({
        title: string()
            .trim()
            .required('Enter a task description')
            .max(255, 'Keep it under 255 characters'),
        status: string().notRequired(),
        priority: string().notRequired(),
        due_date: (savedDueDate
            ? string().required("DrChrono can't remove a due date")
            : string().notRequired()
        ).test(
            'not-past',
            'Pick today or later',
            (value) => !value || value === savedDueDate || value >= format(new Date(), 'yyyy-MM-dd')
        ),
        note: string().trim().notRequired()
    });

// Native date inputs only open from the calendar icon; this opens the picker from anywhere on
// the field. `showPicker` is missing in older browsers and throws in cross-origin iframes.
const openDatePicker = (event: React.MouseEvent<HTMLInputElement>) => {
    try {
        event.currentTarget.showPicker?.();
    } catch {
        // The field still takes typed input.
    }
};

// ui-kit's textarea pads 15px top and bottom, so one row stands taller than an input (5.2rem:
// a 4.8rem line plus borders). A 2.4rem line with 1.2rem padding and the borders matches it.
const SINGLE_ROW_TEXTAREA_STYLES = { padding: '1.2rem 30px' };

const ERROR_STYLES = { color: 'var(--color-fail-alpha)' };

/**
 * The org's statuses, plus the task's own if it has since been archived out of the list. Its
 * name isn't known then (the statuses list is the only source), so it reads as archived.
 */
const getStatusOptions = (statuses: DrChronoTaskStatus[], task: DrChronoTask) => {
    const options = statuses.map(({ id, name }) => ({
        value: String(id),
        label: name || 'Unnamed'
    }));

    return task.status_id && !statuses.some(({ id }) => id === task.status_id)
        ? [...options, { value: String(task.status_id), label: 'Current (archived)' }]
        : options;
};

type FieldsProps = {
    task?: DrChronoTask;
    statuses: DrChronoTaskStatus[];
    isPending: boolean;
    errorMessage?: string;
    onCancel: () => void;
};

const Fields: React.FC<FieldsProps> = ({ task, statuses, isPending, errorMessage, onCancel }) => {
    const { formState, setValue, watch } = useFormContext<TaskFormValues>();
    const hasChanges = !task || Object.keys(toTaskUpdate(watch(), task)).length > 0;
    const [label, pendingLabel] = task ? ['Save', 'Saving...'] : ['Add Task', 'Adding...'];
    // rtm-api can't clear a priority, so a task that has one gets no "Not set" option.
    const canUnsetPriority = !task?.priority;

    return (
        <>
            <TextArea
                name="title"
                displayName="Task description"
                placeholder="e.g. Confirm updated med list"
                maxLength={255}
                rows={2}
                size="large"
                autoFocus
                showErrMessage
                disabled={isPending}
            />
            <div className={Style.formGrid}>
                {task ? (
                    <Select
                        name="status"
                        displayName="Status"
                        noExtraMargin
                        defaultValue={String(task.status_id ?? '')}
                        disabled={isPending}
                    >
                        {getStatusOptions(statuses, task).map(({ value, label }) => (
                            <Option key={value} value={value} labelText={label} />
                        ))}
                    </Select>
                ) : null}
                {/* ui-kit's Select can't hold an empty value, so "Not set" is a sentinel.
                    `noExtraMargin` lines it up with the inputs beside it. */}
                <Select
                    name="priority"
                    displayName="Priority (optional)"
                    noExtraMargin
                    defaultValue={task?.priority ? String(task.priority) : UNSET}
                    disabled={isPending}
                    onChange={(value) =>
                        setValue('priority', value === UNSET ? '' : value, { shouldDirty: true })
                    }
                >
                    {[
                        ...(canUnsetPriority
                            ? [<Option key={UNSET} value={UNSET} labelText="Not set" />]
                            : []),
                        ...TASK_PRIORITIES.map(({ value, label }) => (
                            <Option key={value} value={String(value)} labelText={label} />
                        ))
                    ]}
                </Select>
                <div className={Style.dateField}>
                    <Input
                        name="due_date"
                        displayName="Due date (optional)"
                        type="date"
                        showErrMessage
                        disabled={isPending}
                        onClick={openDatePicker}
                        size="large"
                    />
                </div>
                <TextArea
                    name="note"
                    displayName={task ? 'Add a note (optional)' : 'Note (optional)'}
                    placeholder=""
                    rows={1}
                    size="large"
                    styles={SINGLE_ROW_TEXTAREA_STYLES}
                    disabled={isPending}
                />
            </div>
            {errorMessage ? (
                <InputError id="drchrono-task-error" styles={ERROR_STYLES}>
                    {errorMessage}
                </InputError>
            ) : null}
            <div className={Style.actions}>
                <Button variant="outlineSecondary" size="small" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    size="small"
                    disabled={isPending || !formState.isValid || !hasChanges}
                >
                    {isPending ? pendingLabel : label}
                </Button>
            </div>
        </>
    );
};

type Props = FieldsProps & { onSubmit: (values: TaskFormValues) => void };

/** Creates a task, or edits `task` when one is given. */
const TaskForm: React.FC<Props> = ({ task, onSubmit, ...fieldsProps }) => {
    const defaultValues = React.useMemo(() => toTaskFormValues(task), [task]);
    const schema = React.useMemo(() => getTaskSchema(defaultValues.due_date), [defaultValues]);

    return (
        <div className={Style.form}>
            <Form
                validationSchema={schema}
                defaultValues={defaultValues}
                validationMode="onChange"
                submitFn={onSubmit}
            >
                <Fields task={task} {...fieldsProps} />
            </Form>
        </div>
    );
};

export default TaskForm;
